/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83, Figma 08·04): an audio call of two is the other
 * person's large avatar, their name, "End-to-end encrypted" and the
 * quality of the connection, instead of Element Call's tiles. As soon as
 * either camera is on, or someone else joins, Element Call's view is back
 * (the fallback). Sound does not depend on this view.
 * An outgoing call that rings (08·03) looks the same, with "Вызов…" (then
 * "Не отвечает" / "Звонок отклонён") under the name of the one called.
 */

import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LockSolidIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import { Avatar } from "../Avatar";
import { useBehavior } from "../useBehavior";
import { constant } from "../state/Behavior";
import { type UserMediaViewModel } from "../state/media/UserMediaViewModel";
import { type RingingMediaViewModel } from "../state/media/RingingMediaViewModel";
import { pick } from "./callErrorTexts";
import { CALL_SCREEN_TEXTS as T } from "./callScreen";
import { type IonConnectionQuality } from "./connectionQuality";
import { type IonCallee } from "./awaitingPickup";
import { ionAvatarStyle } from "./avatarColor";
import { QualityBars, QUALITY_TEXT } from "./IonCallSubtitle";
import styles from "./IonAudioCallView.module.css";

const AVATAR_SIZE = 128;
const COMPACT_AVATAR_SIZE = 64;
const OFF$ = constant(false);

interface Props {
  participants: UserMediaViewModel[];
  encrypted: boolean;
  quality: IonConnectionQuality;
  /** The one called, while an outgoing call rings. */
  ringing?: RingingMediaViewModel | null;
  /** The one called, before Element Call starts ringing (awaitingPickup). */
  callee?: IonCallee | null;
  /** Element Call's view, for a video or a group call. */
  fallback: ReactNode;
  /** A small window (picture-in-picture, BUGS 205): the avatar and the name only. */
  compact?: boolean;
}

export const IonAudioCallView: FC<Props> = ({
  participants,
  encrypted,
  quality,
  ringing = null,
  callee = null,
  fallback,
  compact = false,
}) => {
  const { i18n } = useTranslation();
  const l = i18n.language;
  const local = participants.find((p) => p.local);
  const remote = participants.find((p) => !p.local);
  const other = remote ?? ringing ?? undefined;
  const localVideo = useBehavior(local?.videoEnabled$ ?? OFF$);
  const otherVideo = useBehavior(remote?.videoEnabled$ ?? OFF$);
  const otherName = useBehavior(other?.displayName$ ?? EMPTY$);
  const otherAvatar = useBehavior(other?.mxcAvatarUrl$ ?? NO_AVATAR$);
  const name = other ? otherName : (callee?.name ?? "");
  const avatar = other ? otherAvatar : callee?.avatarUrl;
  const userId = other?.userId ?? callee?.userId;
  const speaking = useBehavior(remote?.speaking$ ?? OFF$);
  const pickup = useBehavior(ringing?.pickupState$ ?? RINGING$);

  // A fragment: ReactNode itself may be a promise (React 19 types)
  if (participants.length > 2 || !userId || localVideo || otherVideo)
    return <>{fallback}</>;
  const status = remote
    ? null
    : pickup === "ringing"
      ? T.calling
      : pickup === "timeout"
        ? T.noAnswer
        : T.declined;

  return (
    <div
      className={styles.view}
      data-compact={compact}
      data-testid="ion-audio-call"
    >
      <div className={styles.avatar} data-speaking={speaking}>
        <Avatar
          id={userId}
          name={name}
          src={avatar}
          style={ionAvatarStyle(userId)}
          size={compact ? COMPACT_AVATAR_SIZE : AVATAR_SIZE}
        />
      </div>
      <span className={styles.name}>{name}</span>
      {!compact && status && (
        <span className={styles.status} data-testid="ion-audio-call-status">
          {pick(status, l)}
        </span>
      )}
      {!compact && encrypted && (
        <span className={styles.encrypted}>
          <LockSolidIcon width={16} height={16} aria-hidden />
          {pick(T.encrypted, l)}
        </span>
      )}
      {!compact && remote && quality !== null && (
        <span
          className={styles.quality}
          data-quality={quality}
          data-testid="ion-audio-call-quality"
        >
          <QualityBars quality={quality} />
          {pick(QUALITY_TEXT[quality], l)}
        </span>
      )}
    </div>
  );
};

const EMPTY$ = constant("");
const NO_AVATAR$ = constant<string | undefined>(undefined);
const RINGING$ = constant<"ringing" | "timeout" | "decline">("ringing");
